import { useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import AppTextField from "../../../../../../components/common/forms/AppTextField";
import { useESRApprovalContext } from "../../Context/ESRApprovalContext";
import { useGetTaskTypesQuery } from "../../../../../../hooks/useTaskTypes";
import { ESRESInitialValues, ESRESSchema } from "./utils/schema";
import {
  ESREquipmentServiceColumns,
  ESREquipmentServiceFormFields,
} from "./utils/types";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { appColors } from "../../../../../../theme";
import { useSnackBar } from "../../../../../../components/ToastProvider";
import AppDialog from "../../../../../../components/common/AppDialog";
import AppSelect from "../../../../../../components/common/forms/AppSelect";
import { useGetHeSvPartsQuery } from "../../../../../../hooks/useParts";
import { HeSvParts } from "../../../../../DatabaseManagement/ServicingDBs/Parts/utils/parts-utils";
import { parseDate } from "../../../../../../utils/helpers";

type PartOptionType = {
  id: string | undefined;
  label: string;
  value: string | undefined;
};

const ESREquipmentServiceForm = () => {
  const {
    openESDialog,
    setOpenESDialog,
    esrEquipmentServicesArray,
    setEsrEquipmentServicesArray,
    currentESSelected,
    setCurrentESSelected,
    eSActionType,
  } = useESRApprovalContext();

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<ESREquipmentServiceFormFields>({
    defaultValues: ESRESInitialValues,
    mode: "onChange",
    resolver: yupResolver(ESRESSchema),
  });

  const taskTypes = useGetTaskTypesQuery();
  const taskTypeOptions: any[] = useMemo(() => {
    if (taskTypes?.data === undefined) {
      return [];
    }
    const taskTypeOptionsArray = taskTypes.data
      .map((e) => {
        return {
          id: e.id,
          label: e.taskType,
          value: e.id,
        };
      })
      .filter((option) => option !== null)
      .sort((a, b) => {
        const labelA = a?.label ?? "";
        const labelB = b?.label ?? "";
        return labelA.localeCompare(labelB);
      });

    return taskTypeOptionsArray;
  }, [taskTypes]);

  const { data: heSvParts = [] } = useGetHeSvPartsQuery();

  const heSvPartsOptions = heSvParts
    .map((part) => {
      const formattedPart = {
        id: part.id,
        label: part.partNumber,
        value: part.id,
      };

      return formattedPart;
    })
    .filter(
      (option) =>
        typeof option.label === "string" &&
        option.label.trim() !== "" &&
        option.label !== undefined
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  // Sort, Format, and Filter the heSvParts array. (Might use)
  // function formatHeSvParts(heSvParts: HeSvParts[]): PartOptionType[] {
  //   // Group heSvParts by partNumber and formats updatePeriod to have similar each hesv parts
  //   const groupedByPartNumber = heSvParts.reduce(
  //     (acc: Record<string, HeSvParts[]>, part) => {
  //       // Formats the updatePeriod per heSvPart since the format in database are not the same
  //       const parsedDate = parseDate(String(part.updatePeriod));
  //       if (parsedDate) {
  //         if (!acc[part.partNumber]) {
  //           acc[part.partNumber] = [];
  //         }
  //         acc[part.partNumber].push({ ...part, updatePeriod: parsedDate });
  //       }
  //       return acc;
  //     },
  //     {}
  //   );

  //   // Sort by decending order and returns the most recent part for each group
  //   const mostRecentParts = Object.values(groupedByPartNumber).map(
  //     (group) =>
  //       group.sort(
  //         (a, b) =>
  //           (b.updatePeriod as Date).getTime() -
  //           (a.updatePeriod as Date).getTime()
  //       )[0]
  //   );

  //   // Map to PartOptionType, filter invalid options, and sort alphabetically
  //   return mostRecentParts
  //     .map((part) => ({
  //       id: part.id,
  //       label: part.partNumber,
  //       value: part.id,
  //     }))
  //     .filter(
  //       (option) =>
  //         typeof option.label === "string" &&
  //         option.label.trim() !== "" &&
  //         option.label !== undefined
  //     )
  //     .sort((a, b) => a.label.localeCompare(b.label));
  // }

  // const heSvPartsOptions = formatHeSvParts(heSvParts);

  const handleClose = () => {
    setValue("taskTypeItem", "");
    setValue("taskItem", "");
    setValue("partNumber", "");
    setValue("quantity", "");
    setValue("remarks", "");
    setCurrentESSelected({
      id: "",
      taskItem: "",
      taskTypeId: "",
      taskTypeItem: "",
      partNumber: "",
      quantity: "0",
      remarks: "",
    });
    reset();
    setOpenESDialog(false);
  };

  const onSubmit = (formData: ESREquipmentServiceFormFields) => {
    const number = esrEquipmentServicesArray.length + 1;
    const taskType = taskTypeOptions.find(
      (task) => formData.taskTypeItem == task.id
    );

    const addToArray = () => {
      const newEs: ESREquipmentServiceColumns = {
        id: number.toString(),
        taskItem: formData.taskItem,
        taskTypeId: taskType.id,
        taskTypeItem: taskType.label,
        partNumber: formData.partNumber,
        quantity: Number(formData.quantity).toFixed(2),
        remarks: formData.remarks,
      };

      setEsrEquipmentServicesArray((prev: ESREquipmentServiceColumns[]) => {
        return [...prev, newEs];
      });

      handleClose();
    };

    const updateArray = (
      index: number,
      newValue: ESREquipmentServiceColumns
    ) => {
      const newArray: ESREquipmentServiceColumns[] = [
        ...esrEquipmentServicesArray,
      ];

      newArray[index] = newValue;

      setEsrEquipmentServicesArray(newArray);
      setCurrentESSelected({
        id: "",
        taskItem: "",
        taskTypeId: "",
        taskTypeItem: "",
        partNumber: "",
        quantity: "0",
        remarks: "",
      });
      handleClose();
    };

    if (eSActionType == "new") {
      addToArray();
    }

    if (eSActionType == "update") {
      const newEs: ESREquipmentServiceColumns = {
        id: currentESSelected.id,
        taskItem: formData.taskItem,
        taskTypeId: taskType.id,
        taskTypeItem: taskType.label,
        partNumber: formData.partNumber,
        quantity: Number(formData.quantity).toFixed(2),
        remarks: formData.remarks,
      };

      esrEquipmentServicesArray.forEach((es, index) => {
        if (es.id == newEs.id) {
          updateArray(index, newEs);
        }
      });
    }
  };

  useEffect(() => {
    if (!currentESSelected) {
      console.log("No ES selected");
      return;
    } else {
      setValue("taskItem", currentESSelected.taskItem);
      setValue("taskTypeId", currentESSelected.taskTypeId);
      setValue("taskTypeItem", currentESSelected.taskTypeId);
      setValue("partNumber", currentESSelected.partNumber);
      setValue("quantity", currentESSelected.quantity);
      setValue("remarks", currentESSelected.remarks);
    }
  }, [currentESSelected]);

  return (
    <AppDialog
      open={openESDialog}
      onDialogClose={handleClose}
      title={"Equipment Service"}
      maxWidth="sm"
      sx={{ textAlign: "center" }}
    >
      <Stack
        component="form"
        height="100%"
        bgcolor="white"
        py={1}
        borderRadius={1}
      >
        <Box
          height="100%"
          overflow="auto"
          bgcolor="white"
          gap={2}
          sx={{ paddingTop: ".50em" }}
        >
          <Controller
            control={control}
            name="taskTypeItem"
            render={({ field }) => (
              <AppSelect
                label="Task Type"
                labelAlignment="right"
                labelWidth="20%"
                fieldWidth="80%"
                fontWeight={400}
                placeholder="Select here"
                isOptionsNotSorted={true}
                error={!!errors.taskTypeItem}
                helperText={errors.taskTypeItem?.message}
                options={taskTypeOptions}
                {...register("taskTypeItem")}
                {...field}
              />
            )}
          />

          <AppTextField
            label="Item"
            fontWeight={400}
            labelAlignment="right"
            labelWidth="20%"
            fieldWidth="80%"
            sx={{ marginTop: "4px" }}
            error={!!errors.taskItem}
            helperText={errors.taskItem?.message}
            {...register("taskItem")}
          />
          <Controller
            name="partNumber"
            control={control}
            render={({ field }) => (
              <Stack
                direction="row"
                gap={1.5}
                sx={{ marginTop: "6px" }}
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <Typography
                  color={appColors.formText}
                  component="label"
                  fontSize={15}
                  width="20%"
                  align="right"
                  htmlFor="partNumber"
                  fontWeight={400}
                  whiteSpace="nowrap"
                >
                  Part No
                </Typography>
                <Stack width="80%">
                  <Autocomplete
                    {...field}
                    fullWidth
                    options={heSvPartsOptions}
                    freeSolo
                    filterSelectedOptions
                    filterOptions={(options, { inputValue }) => {
                      const filtered = options.filter((option) =>
                        option.label
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      );
                      // Remove duplicates based on label
                      return filtered.filter(
                        (option, index, self) =>
                          index ===
                          self.findIndex((t) => t.label === option.label)
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        placeholder="Choose or type here"
                        sx={{ width: "100%" }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.label}
                      </li>
                    )}
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        // Handle free solo input
                        field.onChange(newValue);
                      } else if (newValue && "id" in newValue) {
                        // Handle selection of an option
                        field.onChange(newValue.label);
                      } else {
                        // Handle clearing the input
                        field.onChange(null);
                      }
                    }}
                    onInputChange={(event, newInputValue) => {
                      // Only update the input value, don't change the selected option
                      if (event) {
                        field.onChange(newInputValue);
                      }
                    }}
                  />
                </Stack>
              </Stack>
            )}
          />
          <AppTextField
            label="Quantity"
            fontWeight={400}
            labelAlignment="right"
            labelWidth="20%"
            fieldWidth="80%"
            sx={{ marginTop: "4px" }}
            error={!!errors.quantity}
            helperText={errors.quantity?.message}
            {...register("quantity")}
          />
          <AppTextField
            label="Remarks"
            fontWeight={400}
            labelAlignment="right"
            labelWidth="20%"
            fieldWidth="80%"
            sx={{ marginTop: "4px" }}
            error={!!errors.remarks}
            helperText={errors.remarks?.message}
            {...register("remarks")}
          />
          <div style={{ display: "none" }}>
            <AppTextField
              label="taskTypeId"
              required={true}
              error={!!errors.taskTypeId}
              helperText={errors.taskTypeId?.message}
              hidden={true}
              {...register("taskTypeId")}
            />
          </div>
        </Box>
        <Box sx={{ width: "100%", display: "flex" }}>
          <Button
            onClick={handleSubmit(onSubmit)}
            sx={{ width: 150, marginLeft: "auto", marginTop: "1.5em" }}
          >
            Save
          </Button>
        </Box>
      </Stack>
    </AppDialog>
  );
};

export default ESREquipmentServiceForm;
